/**
 * Magic-link / OTP verify flow — extracted from VerifyPage.tsx so the
 * state machine is unit-testable independently of Mantine UI.
 *
 * Pairs with the worker's `/api/auth/recover-token` endpoint
 * (see apps/discovery-dialogues/worker/routes/auth.ts).
 *
 * Why a separate module:
 * - The view used to be 366 lines of mixed UI + auth logic. Coverage
 *   excluded the whole file; regressions in the recover-token retry
 *   path or the next-param sanitisation could only be caught by e2e.
 * - With the logic isolated here, the view becomes thin Mantine glue,
 *   and the testable surface (URL sanitisation, sessionStorage shape,
 *   verify+recover state machine, 403 short-circuit) gets normal unit
 *   tests with a mocked supabase + fetch.
 */

import { apiFetch } from '@/lib/api-client';

export const VERIFY_STORAGE_PREFIX = 'lumi_verify_';

export type VerifyOtpType = 'magiclink' | 'recovery' | 'email' | 'sms';

export type StoredVerifyData = {
  token_hash?: string;
  token?: string;
  uid?: string;
  /** Signed link token (LUM-1083); preferred proof for recover-token. */
  lt?: string;
  email?: string;
  type?: VerifyOtpType | null;
  created_at: number;
};

/**
 * Sanitise a `next` query param so it can only redirect within the
 * same app. Rejects:
 *   - protocol-relative URLs (`//evil.com/x` → `/`)
 *   - cross-origin URLs (`https://attacker.example/x` → `/`)
 *   - URLs with embedded credentials (`https://app/x` with username/password)
 *
 * Returns a path-and-search string starting with `/`.
 */
export function safeNextParam(raw: string | null, origin: string): string {
  if (!raw) return '/';
  if (raw.startsWith('//')) return '/';
  if (raw.startsWith('/')) {
    try {
      const u = new URL(raw, origin);
      return u.pathname + u.search;
    } catch {
      return '/';
    }
  }
  try {
    const base = new URL(origin);
    const u = new URL(raw, origin);
    const sameOrigin = u.origin === base.origin;
    const sameProtocol = u.protocol === base.protocol;
    const noCreds = !(u.username || u.password);
    if (sameOrigin && sameProtocol && noCreds && u.pathname.startsWith('/')) {
      return u.pathname + u.search;
    }
  } catch {
    // ignore
  }
  return '/';
}

/**
 * Mint an opaque state id used as the sessionStorage key for the
 * verify payload. Always backed by Web Crypto:
 *
 *   1. crypto.randomUUID — modern browsers, Workers, Node ≥19.
 *   2. crypto.getRandomValues + hex — for the rare older browser
 *      that ships SubtleCrypto without randomUUID.
 *
 * Throws if neither is available — supabase-js needs crypto for token
 * handling anyway, so a missing crypto means the app is already broken.
 * No Math.random fallback — that would be weaker entropy on a key
 * that, while not a hard secret, names another tab's verify payload.
 */
export function makeStateId(): string {
  const webCrypto =
    typeof globalThis !== 'undefined'
      ? (
          globalThis as typeof globalThis & {
            crypto?: {
              randomUUID?: () => string;
              getRandomValues?: (arr: Uint8Array) => Uint8Array;
            };
          }
        ).crypto
      : undefined;
  if (webCrypto?.randomUUID) return webCrypto.randomUUID();
  if (webCrypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    webCrypto.getRandomValues(bytes);
    return Array.from(bytes)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }
  throw new Error('Web Crypto unavailable — cannot mint verify state id');
}

/** Result of parsing the inbound verify URL. */
export type ParsedVerifyParams = {
  /** Sanitised next path. */
  next: string;
  /** Token hash from the link, if any. */
  tokenHash?: string;
  /** Older `token` param (for legacy links). */
  token?: string;
  /** Supabase auth user id. */
  uid?: string;
  /** Signed link token (LUM-1083). */
  lt?: string;
  /** Email tied to the link, if surfaced. */
  email?: string;
  /** OTP type as advertised by the link; defaults to magiclink downstream. */
  type: VerifyOtpType | null;
  /** Pre-existing state id from a re-render after URL scrub. */
  existingState: string | null;
};

/** Parse the URL params we care about with `next` already sanitised. */
export function parseVerifyParams(
  searchParams: URLSearchParams,
  origin: string,
): ParsedVerifyParams {
  return {
    next: safeNextParam(searchParams.get('next'), origin),
    tokenHash: searchParams.get('token_hash') || undefined,
    token: searchParams.get('token') || undefined,
    type: (searchParams.get('type') as VerifyOtpType | null) || null,
    uid: searchParams.get('uid') || undefined,
    lt: searchParams.get('lt') || undefined,
    email: searchParams.get('email') || undefined,
    existingState: searchParams.get('state'),
  };
}

/**
 * Strip token-bearing params from the URL and substitute `state=<id>`
 * + the sanitised `next` so a refresh / share doesn't re-leak the
 * link. Returns the new href.
 */
export function scrubVerifyUrl(
  href: string,
  next: string,
  stateId: string,
): string {
  const clean = new URL(href);
  clean.searchParams.delete('token_hash');
  clean.searchParams.delete('token');
  clean.searchParams.delete('type');
  clean.searchParams.delete('uid');
  clean.searchParams.delete('lt');
  clean.searchParams.delete('email');
  clean.searchParams.set('next', next);
  clean.searchParams.set('state', stateId);
  return clean.toString();
}

/**
 * Persist the verify payload under the state id. All sessionStorage
 * touches are wrapped in try/catch — quota errors / disabled storage
 * shouldn't crash the verify page; the user can retry from a fresh
 * link instead.
 */
export function saveStoredVerifyData(
  stateId: string,
  data: StoredVerifyData,
  storage: Storage = sessionStorage,
): void {
  try {
    storage.setItem(VERIFY_STORAGE_PREFIX + stateId, JSON.stringify(data));
  } catch {
    // noop
  }
}

export function loadStoredVerifyData(
  stateId: string,
  storage: Storage = sessionStorage,
): StoredVerifyData | null {
  try {
    const raw = storage.getItem(VERIFY_STORAGE_PREFIX + stateId);
    if (!raw) return null;
    return JSON.parse(raw) as StoredVerifyData;
  } catch {
    return null;
  }
}

export function cleanupStoredVerifyData(
  stateId: string | null,
  storage: Storage = sessionStorage,
): void {
  if (!stateId) return;
  try {
    storage.removeItem(VERIFY_STORAGE_PREFIX + stateId);
  } catch {
    // noop
  }
}

export type VerifyOtpFn = (args: {
  type: 'magiclink' | 'recovery';
  token_hash: string;
}) => Promise<{ error?: { status?: number; message?: string } | null }>;

export type TryVerifyResult = {
  /** Did any of the attempted types succeed? */
  ok: boolean;
};

/**
 * Try to verify an OTP/magic-link token under the advertised type
 * first, then fall back to the other "tokenable" types (magiclink /
 * recovery). The fallback exists because Supabase tags links as
 * `recovery` in some flows but the SPA doesn't always know in
 * advance which type the link was minted as.
 *
 * Stops the type-fallback loop on a 403 — no point retrying the same
 * token under a different type once supabase has rejected it. Failure
 * still propagates to the caller, which should fall through to
 * recover-token (the worker can refresh an expired link with its
 * service-role key — the 403 here is *exactly* the trigger for that).
 *
 * `primaryType` is whitelisted to {magiclink, recovery} — the param
 * comes from URL searchParams, so a tampered or unfamiliar value
 * (`?type=email`, `?type=garbage`) collapses to magiclink rather than
 * being passed verbatim to verifyOtp.
 */
export async function tryVerifyOtp(
  verifyOtp: VerifyOtpFn,
  tokenHash: string,
  primaryType: string,
): Promise<TryVerifyResult> {
  const normalisedPrimary: 'magiclink' | 'recovery' =
    primaryType === 'recovery' ? 'recovery' : 'magiclink';
  const tryTypes: Array<'magiclink' | 'recovery'> =
    normalisedPrimary === 'recovery'
      ? ['recovery', 'magiclink']
      : ['magiclink', 'recovery'];

  for (const ty of tryTypes) {
    const { error } = await verifyOtp({ type: ty, token_hash: tokenHash });
    if (!error) return { ok: true };
    if (error?.status === 403) return { ok: false };
  }
  return { ok: false };
}

/** What recover-token accepts as proof of the original link. */
export type RecoveryProof =
  | { lt: string }
  | { uid: string; expired_token_hash: string };

/**
 * Ask the worker for a fresh token_hash. Prefers the signed `lt`; links
 * emailed before it existed fall back to the legacy uid +
 * expired-hash body, which never earns a fresh token (the worker only
 * returns that hash while Supabase still accepts it, and only until its
 * LEGACY_UID_RECOVERY_UNTIL cutoff). Returns null on any non-2xx or
 * transport error so the caller surfaces a generic "request a new
 * link" error instead of leaking server-side detail.
 */
export async function recoverToken(
  proof: RecoveryProof,
  fetcher: typeof apiFetch = apiFetch,
): Promise<string | null> {
  try {
    const resp = await fetcher('/api/auth/recover-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(proof),
    });
    if (!resp.ok) return null;
    const data: { token_hash?: string } = await resp.json();
    return data.token_hash ?? null;
  } catch {
    return null;
  }
}

export type VerifyFlowOutcome =
  | { kind: 'success' }
  | { kind: 'no_state' }
  | { kind: 'no_token' }
  | { kind: 'expired' }
  | { kind: 'unknown_error'; message: string };

export type VerifyFlowDeps = {
  /** Bound supabase verifyOtp. */
  verifyOtp: VerifyOtpFn;
  /** Token recovery fetch. Defaults to the live one. */
  recover?: typeof recoverToken;
  /** Storage backing the verify payload. Defaults to sessionStorage. */
  storage?: Storage;
};

/**
 * Run the full verify state machine for a given state id:
 *
 *   1. Read the stored payload from sessionStorage.
 *   2. Try `verifyOtp` under the advertised type, then fallbacks.
 *   3. On any failure with an `lt` (or, for older links, a `uid`) →
 *      ask recover-token for a fresh hash, retry. (A 403 from supabase
 *      is *exactly* the trigger for recover-token: the worker has the
 *      service-role key and can mint a new hash for the same user.)
 *   4. Cleanup sessionStorage on success; leave it alone on transient
 *      failure so a refresh can retry.
 *
 * The view is responsible for navigating after success and rendering
 * the error copy.
 */
export async function runVerifyFlow(
  stateId: string | null,
  deps: VerifyFlowDeps,
): Promise<VerifyFlowOutcome> {
  const storage = deps.storage ?? sessionStorage;
  const recover = deps.recover ?? recoverToken;

  if (!stateId) return { kind: 'no_state' };

  const stored = loadStoredVerifyData(stateId, storage);
  const hash = stored?.token_hash || stored?.token;
  const uid = stored?.uid;
  const lt = stored?.lt;
  const t = stored?.type ?? 'magiclink';

  if (!hash) return { kind: 'no_token' };

  const first = await tryVerifyOtp(deps.verifyOtp, hash, t);
  if (first.ok) {
    cleanupStoredVerifyData(stateId, storage);
    return { kind: 'success' };
  }

  let proof: RecoveryProof | null = null;
  if (lt) proof = { lt };
  else if (uid) proof = { uid, expired_token_hash: hash };
  if (proof) {
    const freshHash = await recover(proof);
    if (freshHash) {
      const retry = await tryVerifyOtp(deps.verifyOtp, freshHash, t);
      if (retry.ok) {
        cleanupStoredVerifyData(stateId, storage);
        return { kind: 'success' };
      }
    }
  }

  return { kind: 'expired' };
}
